// Adding a secret without leaving the wizard: the Secrets page's drawer, opened on the template
// for the kind needed, and the new secret chosen where it was asked for. Only a linked device
// can add one (it seals the value and signs the request).
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

import { useDevice } from "../../device/context";
import { useT } from "../../i18n";
import { toast } from "../../layout/toast";
import { AddSecretDrawer } from "../../secrets/AddSecretDrawer";
import { type Credential, templateOf, type TemplateId } from "../../secrets/templates";

/** Where the new secret goes: the source, or a requirement by index. */
export type AddTarget = "source" | number;

export function useAddSecret(orgId: string | undefined, onSaved: (credential: Credential, target: AddTarget) => void) {
  const t = useT().tasks.create.secrets;
  const device = useDevice();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState<{ template: TemplateId; target: AddTarget } | null>(null);
  if (device.kind !== "linked" || !orgId) return { open: undefined, drawer: null as ReactNode };
  const signer = { proto: device.proto, deviceId: device.device.deviceId, key: device.device.keys.auth.privateKey };
  const open = (kind: string, target: AddTarget) => setAdding({ template: templateOf({ kind, injection: null })?.id ?? "other", target });
  const drawer = (
    <AddSecretDrawer
      key={adding?.template ?? "closed"}
      open={adding !== null}
      initial={adding?.template}
      onClose={() => setAdding(null)}
      signer={signer}
      signerName={device.device.name}
      orgId={orgId}
      onSaved={(credential) => {
        if (adding) onSaved(credential, adding.target);
        toast(t.saved(credential.name));
        void queryClient.invalidateQueries({ queryKey: ["credentials"] });
      }}
      onError={(message) => toast(message, "warn")}
    />
  );
  return { open, drawer };
}
